import filesystem from './templates/file-system';
import smartCapture from './templates/smart-capture';
import gateway from './templates/gateway';
import camera from './templates/camera';
import view from './templates/view';
import passiveLiveness from './templates/passive-liveness';
import _get from 'lodash/get';
import './styles.sass';

const DEFAULT_ASSETS_DIRECTORY = '/assets/id-scan';

/**
 * A class to handle common requirements of the id scan SDK.
 * To begin an IDScan journey, call the start method on this class.
 *
 * This assumes the provided assetsDirectory will contain these files:
 * - ides-micro.js (smart capture requirement)
 * - idesmicro_asm.js (smart capture requirement)
 * - idesmicro_asm.wasm (smart capture requirement)
 * - idscan-jcs.js (primary id-scan library file)
 * - styles.sass (any custom styles to be loaded globally)
 */
export default class IDScan {
  constructor(initialConfig = {}, onCallback = () => {}, onError = () => {}) {
    const assetsDirectory = initialConfig.assetsDirectory || DEFAULT_ASSETS_DIRECTORY;
    const initialTemplates = initialConfig.templates || {};
    this.config = {
      backendUrl: undefined,
      assetsDirectory,
      container: '#id-scan',
      fileUploadOnCameraCaptureJourneys: true,
      previewPages: {
        documentStep: {
          smartCapture: false,
          manualCapture: false
        },
        facematchStep: {
          manualCapture: false
        },
        poaStep: {
          manualCapture: false
        },
        passiveLivenessStep: {
          manualCapture: false
        }
      },
      manualCapture: {
        enabled: true,
        timeout: 15
      },
      templates: {
        ...initialTemplates,
        view: initialTemplates.view || view({ assetsDirectory }),
        passiveLiveness: initialTemplates.passiveLiveness || passiveLiveness(),
        camera: initialTemplates.camera || camera(),
        gateway: initialTemplates.gateway || gateway(),
        smartCapture: initialTemplates.smartCapture || smartCapture(),
        filesystem: initialTemplates.filesystem || filesystem({ assetsDirectory })
      },
      token: undefined,
      smartCapture: {
        workerScriptUrl: `${assetsDirectory}/ides-micro.39fea1d510102d6d3830.js`,
        asmScriptUrl: `${assetsDirectory}/idesmicro_asm.js`,
        timeout: 10000
      },
      ...initialConfig,
      onJourneyEvent: this.onJourneyEvent(onCallback, onError)
    };
    this.eventJourney = {};
    this.retrieval = undefined;
    this.images = {};
    this.journeyIdInput = null;
  }

  /**
   * Wrapper for the journey event callback to parse and provide information on
   * errors. This will still provide the standard SDK event, states and meta
   *
   * Documentation can be found here: {@link https://docs.idscan.com/docs/websdk-21-4/sdk/enumerations/journeyevent/}
   *
   * @param onCallback Function to call if there is no error
   * @param onError Function to call if there is an error
   * @returns {function(*=, *, *): undefined}
   */
  onJourneyEvent(onCallback, onError) {
    return (event = '', states = {}, meta = {}) => {
      if (!event) {
        return;
      }

      // Ensure the latest event journey is always accessible in the class
      this.eventJourney = meta.journey || {};
      const finished = this.eventJourney.isFinished;

      if (this.eventHasError(event)) {
        onError(event);
        return;
      }

      onCallback({ event, states, meta }, finished);
    };
  }

  eventHasError(event) {
    const errorKeys = ['exception', 'failed'];
    for (let i = 0; i < errorKeys.length; i++) {
      if (event.toLowerCase().includes(errorKeys[i])) {
        return true;
      }
    }
  }

  /**
   * Define the journey ID
   * @returns {*}
   */
  get journeyId() {
    return _get(this, 'JourneyContainer._journey.id');
  }

  /**
   * Define the Journey Reference String
   * @returns {*}
   */
  get journeyReference() {
    return this.eventJourney.journeyReference;
  }

  get person() {
    const person = this.eventJourney.person || {};
    return {
      firstName: person.firstName || '',
      lastName: person.lastName || '',
      middleName: person.middleName || '',
      birthDate: person.birthDate || ''
    };
  }

  get address() {
    return _get(this, 'eventJourney.person.address') || {};
  }

  get documents() {
    return this.eventJourney.documents || [];
  }

  async getRetrieval() {
    if (!this.journeyId) {
      return {};
    }

    const url = `${this.config.backendUrl}/idscanenterprisesvc/journey/get?journeyId=${this.journeyId}`;
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        'accept': 'application/json',
        'Authorization': this.config.investigationToken
      }
    });
    this.retrieval = await response.json();
    return this.retrieval;
  }

  async getDocumentImages() {
    // As this requires retrieval data we need to ensure it has been fetched
    if (!this.retrieval) {
      await this.getRetrieval();
    }

    // If there is no journey ID yet, there can be no images
    if (!this.retrieval.JourneyId) {
      return {};
    }

    const documents = this.retrieval.ProcessedDocuments;

    for (let i = 0; i < documents.length; i++) {
      let document = documents[i];

      // Skip if the document is a selfie
      if (document.DocumentCategory === 'Selfie') {
        continue;
      }

      let category = document.DocumentCategory.replaceAll(' ', '');
      let key = [category, document.DocumentSide].filter(Boolean).join('-');
      // Skip retrieving an image that has already been retrieved
      if (this.images[key]) {
        continue;
      }

      let whiteImage = document.DocumentImages.find((di) => di.Role === 'WhiteImage');
      // Skip if the document image has no white image
      if (!whiteImage) {
        continue;
      }

      // Fetch the image
      let response = await fetch(whiteImage.Url, {
        method: 'GET',
        headers: {
          Authorization: this.config.investigationToken
        }
      });

      // Set the image / related data
      let type = response.headers.get('content-type') || '';
      let splitType = type.split('/');
      let name = `${key}.${splitType[1]}`.replace(' ', '');
      let blob = await response.blob();
      this.images[key] = new File([blob], name, { type });
    }

    return this.images;
  }

  /**
   * Return key information about the state of the journey including
   * - The Reference and ID of the journey in progress
   * - The users information
   */
  get results() {
    if (!this.eventJourney) {
      return {};
    }

    return {
      journeyId: this.journeyId,
      journeyReference: this.journeyReference,
      person: this.person,
      address: this.address,
      documents: this.documents
    };
  }

  /**
   * Start a GBG IDScan journey. This will:
   * - Ensure all required JS/CSS files are loaded
   * - Instantiate the JourneyContainer and bind it to this class
   * - Start the journey
   *
   * @returns {*}
   */
  async start() {
    return new Promise(async (resolve, reject) => {
      try {
        // Ensure the mount element is available
        if (!document.querySelector(this.config.container)) {
          return reject('Mount element not defined');
        }

        // Ensure the primary JS library and styles are loaded
        if (!window.GBG || !window.GBG.Idscan) {
          await this.config.loadScript(`${this.config.assetsDirectory}/idscan-jcs.b3477c84f99d0979335b.js`);

          if (!window.GBG || !window.GBG.Idscan) {
            return reject('Failed to define GBG IDScan');
          }
        }

        // Instantiate and mount the IDScan Journey Container
        this.JourneyContainer = new window.GBG.Idscan.JourneyContainer(this.config);
        this.JourneyContainer.initialize();
        return resolve();
      } catch (e) {
        return reject(e);
      }
    });
  }
}
